/* --------------------------------------------------
                Google Maps Manager
-------------------------------------------------- */
class GoogleMapsManager {
  constructor() {
    this.apiLoaded = false;
    this.loadingPromise = null;
    this.activeMaps = new Map();
    this.mapCache = new Map(); // Cache for map instances
    this.lazyMaps = new Map(); // Track lazy-loaded maps
    this.usageStats = {
      apiLoads: 0,
      mapCreations: 0,
      mapDestructions: 0,
      cacheHits: 0,
      cacheMisses: 0,
      lazyLoads: 0,
      errors: 0
    };
    this.loadPersistentCache();
    this.setupTurboHandlers();
    this.setupIntersectionObserver();
  }

  async loadAPI(apiKey, libraries = ['marker']) {
    if (this.apiLoaded) {
      return Promise.resolve();
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = new Promise((resolve, reject) => {
      if (typeof google !== 'undefined') {
        this.apiLoaded = true;
        this.trackApiLoad();
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${libraries.join(',')}&v=beta`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.apiLoaded = true;
        this.trackApiLoad();
        resolve();
      };

      script.onerror = () => {
        this.loadingPromise = null;
        this.usageStats.errors++;
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    });

    return this.loadingPromise;
  }

  registerMap(id, mapInstance) {
    this.activeMaps.set(id, mapInstance);
    this.trackMapCreation();
  }

  // Cache a map instance for reuse
  cacheMap(id, mapData) {
    const cacheKey = this.generateCacheKey(mapData);
    this.mapCache.set(cacheKey, {
      id,
      mapData,
      timestamp: Date.now(),
      usageCount: 0
    });
    this.savePersistentCache();
  }

  // Try to get a cached map instance
  getCachedMap(mapData, newestJobUpdatedAt = null) {
    const cacheKey = this.generateCacheKey(mapData);
    const cached = this.mapCache.get(cacheKey);

    if (cached && this.isCacheValid(cached, newestJobUpdatedAt)) {
      cached.usageCount++;
      this.trackCacheHit();
      return cached;
    }

    this.trackCacheMiss();
    return null;
  }

  // Generate cache key based on map configuration
  generateCacheKey(mapData) {
    // If a custom cache key is provided, use it
    if (mapData.cacheKey) {
      return mapData.cacheKey;
    }

    // Otherwise, generate key from configuration
    const key = {
      center: mapData.center ? {
        lat: Math.round(mapData.center.lat * 1000000) / 1000000, // Round to 6 decimal places
        lng: Math.round(mapData.center.lng * 1000000) / 1000000
      } : mapData.center,
      zoom: mapData.zoom,
      mapTypeId: mapData.mapTypeId,
      styles: mapData.styles,
      mapId: mapData.mapId // Include mapId in cache key
    };
    return JSON.stringify(key);
  }

  // Check if cache entry is still valid (1 hour TTL, or invalidated by newer job)
  isCacheValid(cached, newestJobUpdatedAt = null) {
    const TTL = 60 * 60 * 1000; // 1 hour

    // Check TTL first
    const isWithinTTL = (Date.now() - cached.timestamp) < TTL;
    if (!isWithinTTL) {
      return false;
    }

    // If newestJobUpdatedAt is provided, check if any job is newer than cache
    if (newestJobUpdatedAt) {
      const cacheTime = new Date(cached.timestamp);
      const newestJobTime = new Date(newestJobUpdatedAt);
      // If a job is newer than the cache, invalidate it
      if (newestJobTime > cacheTime) {
        return false;
      }
    }

    return true;
  }

  // Clear expired cache entries
  clearExpiredCache() {
    for (const [key, cached] of this.mapCache.entries()) {
      if (!this.isCacheValid(cached, null)) {
        this.mapCache.delete(key);
      }
    }
  }

  destroyMap(id) {
    const mapData = this.activeMaps.get(id);
    if (mapData) {
      // Clean up markers
      if (mapData.markers && Array.isArray(mapData.markers)) {
        mapData.markers.forEach(marker => {
          if (marker.setMap) {
            marker.setMap(null);
          }
        });
      }

      // Clean up info window
      if (mapData.infoWindow && mapData.infoWindow.close) {
        mapData.infoWindow.close();
      }

      // Clear map instance
      if (mapData.map && mapData.map.setMap) {
        mapData.map.setMap(null);
      }

      this.activeMaps.delete(id);
      this.trackMapDestruction();
    }
  }

  destroyAllMaps() {
    this.activeMaps.forEach((mapData, id) => {
      this.destroyMap(id);
    });
  }

  setupTurboHandlers() {
    // Clean up maps before caching
    document.addEventListener('turbo:before-cache', () => {
      this.destroyAllMaps();
      this.clearExpiredCache();
    });

    // Clean up maps before rendering new content
    document.addEventListener('turbo:before-render', () => {
      this.destroyAllMaps();
    });

    // Clean up maps when navigating away
    document.addEventListener('turbo:before-visit', () => {
      this.destroyAllMaps();
    });

    // Periodic cache cleanup (every 2 minutes)
    setInterval(() => {
      this.clearExpiredCache();
    }, 2 * 60 * 1000);
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      return; // Fallback for older browsers
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadLazyMap(entry.target);
        }
      });
    }, {
      rootMargin: '50px', // Load when 50px away from viewport
      threshold: 0.1
    });
  }

  trackApiLoad() {
    this.usageStats.apiLoads++;
  }

  trackMapCreation() {
    this.usageStats.mapCreations++;
  }

  trackMapDestruction() {
    this.usageStats.mapDestructions++;
  }

  trackCacheHit() {
    this.usageStats.cacheHits++;
    this.savePersistentCache();
  }

  trackCacheMiss() {
    this.usageStats.cacheMisses++;
    this.savePersistentCache();
  }

  trackLazyLoad() {
    this.usageStats.lazyLoads++;
    this.savePersistentCache();
  }

  logUsage() {
    if (window.console && window.console.log) {
      console.log('🗺️ Google Maps Usage:', this.usageStats);
    }
  }

  getStats() {
    return { ...this.usageStats };
  }

  getCacheStats() {
    return {
      cacheSize: this.mapCache.size,
      totalCacheHits: this.usageStats.cacheHits,
      totalCacheMisses: this.usageStats.cacheMisses,
      cacheHitRate: this.usageStats.cacheHits / (this.usageStats.cacheHits + this.usageStats.cacheMisses) * 100 || 0
    };
  }

  // Clear all cache entries
  clearCache() {
    this.mapCache.clear();
    this.savePersistentCache();
  }

  // Clear persistent cache from sessionStorage
  clearPersistentCache() {
    try {
      sessionStorage.removeItem('googleMapsCache');
      sessionStorage.removeItem('googleMapsStats');
      this.mapCache.clear();
      this.usageStats = {
        apiLoads: 0,
        mapCreations: 0,
        mapDestructions: 0,
        cacheHits: 0,
        cacheMisses: 0,
        lazyLoads: 0,
        errors: 0
      };
      console.log('🗺️ Persistent cache cleared');
    } catch (error) {
      console.warn('Failed to clear persistent cache:', error);
    }
  }

  // Load cache from sessionStorage
  loadPersistentCache() {
    try {
      const cached = sessionStorage.getItem('googleMapsCache');
      if (cached) {
        const cacheData = JSON.parse(cached);
        // Restore cache entries that are still valid
        for (const [key, entry] of Object.entries(cacheData)) {
          if (this.isCacheValid(entry)) {
            this.mapCache.set(key, entry);
          }
        }
      }

      // Load usage stats
      const stats = sessionStorage.getItem('googleMapsStats');
      if (stats) {
        this.usageStats = { ...this.usageStats, ...JSON.parse(stats) };
      }
    } catch (error) {
      console.warn('Failed to load persistent cache:', error);
    }
  }

  // Save cache to sessionStorage
  savePersistentCache() {
    try {
      const cacheData = {};
      for (const [key, entry] of this.mapCache.entries()) {
        cacheData[key] = entry;
      }
      sessionStorage.setItem('googleMapsCache', JSON.stringify(cacheData));
      sessionStorage.setItem('googleMapsStats', JSON.stringify(this.usageStats));
    } catch (error) {
      console.warn('Failed to save persistent cache:', error);
    }
  }

  // Helper method to create a map with caching support
  createMapWithCache(container, mapOptions, id, newestJobUpdatedAt = null) {
    // Try to get cached map first
    const cached = this.getCachedMap(mapOptions, newestJobUpdatedAt);
    if (cached) {
      // Reuse cached map configuration
      const map = new google.maps.Map(container, cached.mapData);
      this.registerMap(id, { map });
      return map;
    }

    // Create new map and cache it
    const map = new google.maps.Map(container, mapOptions);
    this.cacheMap(id, mapOptions);
    this.registerMap(id, { map });
    return map;
  }

  // Register a map for lazy loading
  registerLazyMap(container, mapOptions, id, callback) {
    if (!this.intersectionObserver) {
      // Fallback: load immediately if IntersectionObserver not supported
      this.loadLazyMap(container, mapOptions, id, callback);
      return;
    }

    // Store lazy map configuration
    this.lazyMaps.set(container, { mapOptions, id, callback });

    // Start observing the container
    this.intersectionObserver.observe(container);
  }

  // Load a lazy map when it becomes visible
  async loadLazyMap(container, mapOptions = null, id = null, callback = null) {
    // Get stored configuration if not provided
    const stored = this.lazyMaps.get(container);
    if (stored) {
      mapOptions = stored.mapOptions;
      id = stored.id;
      callback = stored.callback;
      this.lazyMaps.delete(container);
    }

    if (!mapOptions || !id) {
      return;
    }

    try {
      // Load API if needed
      await this.loadAPI(container.dataset.apiKey);

      // Create map with caching
      const map = this.createMapWithCache(container, mapOptions, id);

      // Track lazy load
      this.trackLazyLoad();

      // Call callback if provided
      if (callback && typeof callback === 'function') {
        callback(map);
      }

      // Stop observing this container
      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(container);
      }
    } catch (error) {
      console.error('Failed to load lazy map:', error);
    }
  }
}

// Create global instance
window.googleMapsManager = new GoogleMapsManager();

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GoogleMapsManager;
}
